import React, { Suspense } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, Tabs, Spin, Button, Space, Tag } from '@arco-design/web-react';
import { IconRefresh } from '@arco-design/web-react/icon';
import ErrorBoundary from '@/modules/core/error-handling/ErrorBoundary';
import ModuleHeader from '@/modules/common/components/layout/ModuleHeader';
import KpiCards from './revenue/KpiCards';
import { useKPIs, useReconciliationStatus } from '../hooks/useReports';

const PayoutsList = React.lazy(() => import('./revenue/PayoutsList'));

const TabPane = Tabs.TabPane;

const TabFallback = () => (
  <div style={{ padding: 40, textAlign: 'center' }}>
    <Spin size={30} />
  </div>
);

const RevenuePage = () => {
  const { t } = useTranslation('finance');
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'kpis';

  const { data: kpis, isLoading: kpisLoading, refetch: refetchKpis } = useKPIs();
  const { data: reconciliation } = useReconciliationStatus();

  const handleTabChange = (tab) => {
    setSearchParams({ tab }, { replace: true });
  };

  return (
    <div style={{ padding: 0 }}>
      <Tabs
        activeTab={activeTab}
        onChange={handleTabChange}
        lazyload
        destroyOnHide={false}
        style={{ marginTop: 8 }}
      >
        <TabPane key="kpis" title={t('tabs.dashboardKpis')}>
          <ErrorBoundary>
            <Card style={{ marginTop: 8 }}>
              <ModuleHeader
                title={t('revenue.kpisTitle')}
                inCard
                stats={
                  kpis?.generated_at && (
                    <Tag color="gray">
                      {t('revenue.updated')} {new Date(kpis.generated_at).toLocaleString('es-MX')}
                    </Tag>
                  )
                }
                actions={<Button icon={<IconRefresh />} onClick={refetchKpis} />}
              />
              <div style={{ marginTop: 16 }}>
                <KpiCards kpis={kpis} loading={kpisLoading} />
              </div>

              {reconciliation && (
                <Card style={{ marginTop: 16 }} title={t('revenue.reconciliation.title')}>
                  <Space size="large">
                    <Tag color="orange">{t('revenue.reconciliation.pending')} {reconciliation.pending || 0}</Tag>
                    <Tag color="green">{t('revenue.reconciliation.matched')} {reconciliation.matched || 0}</Tag>
                    <Tag color="red">{t('revenue.reconciliation.exception')} {reconciliation.exception || 0}</Tag>
                    <Tag color="blue">{t('revenue.reconciliation.manual')} {reconciliation.manual || 0}</Tag>
                    <Tag color="arcoblue">{t('revenue.reconciliation.total')} {reconciliation.total || 0}</Tag>
                  </Space>
                </Card>
              )}
            </Card>
          </ErrorBoundary>
        </TabPane>

        <TabPane key="payouts" title={t('tabs.payouts')}>
          <ErrorBoundary>
            <Suspense fallback={<TabFallback />}>
              <Card style={{ marginTop: 8 }}>
                <PayoutsList />
              </Card>
            </Suspense>
          </ErrorBoundary>
        </TabPane>

      </Tabs>
    </div>
  );
};

export default RevenuePage;
